// src/pages/Orders/OrderForm/OrderFormSections/OrderSourceIndicator.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import Badge from "../../../../components/common/Badge";
import Button from "../../../../components/common/Button";
import {
  FiShoppingCart,
  FiUsers,
  FiPackage,
  FiEdit3,
  FiInfo,
  FiLock,
  FiPercent,
  FiClock,
  FiDollarSign,
} from "react-icons/fi";

const OrderSourceIndicator = ({
  source, // "direct", "cart", "session"
  sourceData,
  formatCurrency,
  formatDate,
}) => {
  const navigate = useNavigate();

  const handleEditSession = () => {
    if (sourceData?.sessionId) {
      navigate(`/consultations/${sourceData.sessionId}`, {
        state: { returnToOrder: true },
      });
    }
  };

  // Direct Order
  if (source === "direct") {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <FiPackage className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-1">
              Direct Order Creation
            </h3>
            <p className="text-sm text-blue-700">
              Creating a new order from scratch with fresh product selection and
              real-time pricing.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Cart Order
  if (source === "cart" || source === "from-cart") {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <FiShoppingCart className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-green-900">
                Creating Order from Cart
              </h3>
              <Badge variant="success" size="sm">
                Cart Linked
              </Badge>
            </div>

            <p className="text-sm text-green-700 mb-3">
              Items and adjustments have been imported from customer's cart.
            </p>

            {/* Cart Details Grid */}
            <div className="grid grid-cols-4 gap-3 text-xs">
              {/* Items Count */}
              <div className="bg-white bg-opacity-50 rounded p-2">
                <div className="flex items-center gap-1 text-green-800 mb-1">
                  <FiShoppingCart className="w-3 h-3" />
                  <span className="font-medium">Items</span>
                </div>
                <p className="text-green-900 font-semibold">
                  {sourceData?.items?.length || 0}
                </p>
              </div>

              {/* Coupons Applied */}
              <div className="bg-white bg-opacity-50 rounded p-2">
                <div className="flex items-center gap-1 text-green-800 mb-1">
                  <FiPercent className="w-3 h-3" />
                  <span className="font-medium">Coupons</span>
                </div>
                <p className="text-green-900 font-semibold">
                  {sourceData?.appliedCoupons?.length || 0}
                </p>
              </div>

              {/* Exchange Items */}
              <div className="bg-white bg-opacity-50 rounded p-2">
                <div className="flex items-center gap-1 text-green-800 mb-1">
                  <FiUsers className="w-3 h-3" />
                  <span className="font-medium">Exchange</span>
                </div>
                <p className="text-green-900 font-semibold">
                  {sourceData?.exchangeItems?.length || 0}
                </p>
              </div>

              {/* Cart Value */}
              <div className="bg-white bg-opacity-50 rounded p-2">
                <div className="flex items-center gap-1 text-green-800 mb-1">
                  <FiDollarSign className="w-3 h-3" />
                  <span className="font-medium">Value</span>
                </div>
                <p className="text-green-900 font-semibold">
                  {formatCurrency(sourceData?.totalValue || 0)}
                </p>
              </div>
            </div>

            {/* Session Link if available */}
            {sourceData?.sessionId && (
              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                <FiInfo className="inline w-3 h-3 mr-1" />
                Cart is linked to consultation session:{" "}
                <strong>{sourceData.sessionCode || "Unknown"}</strong>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Session Order
  if (source === "session" || source === "from-session") {
    const priceLock = sourceData?.session?.priceLock || sourceData?.priceLock;
    const priceAdjustments =
      sourceData?.session?.priceAdjustments ||
      sourceData?.priceAdjustments ||
      [];
    const activeAdjustments = priceAdjustments.filter(
      (adj) => adj.status === "ACTIVE"
    );

    return (
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <FiUsers className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-purple-900">
                  Creating Order from Consultation Session
                </h3>
                <Badge variant="purple" size="sm">
                  Session Linked
                </Badge>
              </div>

              {/* Edit Session Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleEditSession}
                className="border-purple-300 text-purple-700 hover:bg-purple-100"
              >
                <FiEdit3 className="w-3 h-3 mr-1" />
                Edit Session
              </Button>
            </div>

            <p className="text-sm text-purple-700 mb-3">
              Data has been imported from consultation session{" "}
              <strong>{sourceData?.sessionCode}</strong>
            </p>

            {/* Session Details Grid */}
            <div className="grid grid-cols-4 gap-3 text-xs mb-3">
              {/* Cart Items */}
              <div className="bg-white bg-opacity-50 rounded p-2">
                <div className="flex items-center gap-1 text-purple-800 mb-1">
                  <FiShoppingCart className="w-3 h-3" />
                  <span className="font-medium">Cart Items</span>
                </div>
                <p className="text-purple-900 font-semibold">
                  {sourceData?.session?.outcome?.itemsAddedToCart ||
                    sourceData?.items?.length ||
                    0}
                </p>
              </div>

              {/* Exchange Items */}
              <div className="bg-white bg-opacity-50 rounded p-2">
                <div className="flex items-center gap-1 text-purple-800 mb-1">
                  <FiUsers className="w-3 h-3" />
                  <span className="font-medium">Exchange</span>
                </div>
                <p className="text-purple-900 font-semibold">
                  {sourceData?.session?.exchangeItems?.filter(
                    (i) => i.customerAccepted
                  ).length ||
                    sourceData?.exchangeItems?.length ||
                    0}
                </p>
              </div>

              {/* Adjustments */}
              <div className="bg-white bg-opacity-50 rounded p-2">
                <div className="flex items-center gap-1 text-purple-800 mb-1">
                  <FiPercent className="w-3 h-3" />
                  <span className="font-medium">Adjustments</span>
                </div>
                <p className="text-purple-900 font-semibold">
                  {activeAdjustments.length}
                </p>
              </div>

              {/* Session Value */}
              <div className="bg-white bg-opacity-50 rounded p-2">
                <div className="flex items-center gap-1 text-purple-800 mb-1">
                  <FiDollarSign className="w-3 h-3" />
                  <span className="font-medium">Value</span>
                </div>
                <p className="text-purple-900 font-semibold">
                  {formatCurrency(
                    priceLock?.lockedPrices?.summary?.grandTotal ||
                      sourceData?.session?.initialCartSnapshot?.totalValue ||
                      0
                  )}
                </p>
              </div>
            </div>

            {/* Price Lock Info */}
            {priceLock?.isLocked && (
              <div className="p-2 bg-yellow-50 border border-yellow-200 rounded mb-2">
                <div className="flex items-start gap-2">
                  <FiLock className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 text-xs">
                    <p className="font-semibold text-yellow-900 mb-1">
                      Prices Locked from Session
                    </p>
                    <div className="flex items-center justify-between text-yellow-700">
                      <span>Locked by: {priceLock.lockedBy || "Admin"}</span>
                      <span className="flex items-center gap-1">
                        <FiClock className="w-3 h-3" />
                        Expires: {formatDate(priceLock.lockExpiresAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Session Adjustments Info */}
            {activeAdjustments.length > 0 && (
              <div className="p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                <FiPercent className="inline w-3 h-3 mr-1" />
                {activeAdjustments.length} price adjustment(s) will be applied
                from the consultation session
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OrderSourceIndicator;
